import { useState } from "react";
import api from "@/lib/api";

export default function PracticeModal({ ctx, onClose }) {
  const [phase, setPhase] = useState("intro"); // intro | loading | quiz | grading | result
  const [questions, setQuestions] = useState([]);
  const [quizId, setQuizId] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  async function startQuiz() {
    setPhase("loading"); setError("");
    try {
      const { data } = await api.post("/practice/quiz", ctx);
      setQuestions(data.questions); setQuizId(data.quiz_id);
      setAnswers({}); setPhase("quiz");
    } catch (e) { setError(e.response?.data?.detail || e.message); setPhase("intro"); }
  }
  async function submit() {
    setPhase("grading");
    try {
      const { data } = await api.post("/practice/grade", { quiz_id: quizId, answers });
      setResult(data); setPhase("result");
    } catch (e) { setError(e.response?.data?.detail || e.message); setPhase("quiz"); }
  }

  return (
    <div data-testid="practice-modal" className="fixed inset-0 bg-black/85 backdrop-blur-md z-50 flex items-center justify-center p-4">
      <div className="bg-[#151a24] border border-slate-800 rounded-2xl max-w-2xl w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-4 border-b border-slate-800 pb-3">
          <div>
            <h2 className="text-lg font-bold text-white flex items-center gap-2 font-mono">
              <i className="fa-solid fa-graduation-cap text-purple-400"/> ICT Practice Mode
              <span className="ml-1 px-2 py-0.5 text-[10px] rounded bg-purple-500/10 text-purple-300 border border-purple-500/30">Claude Sonnet 4.5</span>
            </h2>
            <p className="text-xs text-slate-400 font-mono">Test your ICT chart-reading on the current snapshot</p>
          </div>
          <button data-testid="practice-close" onClick={onClose} className="text-slate-500 hover:text-white"><i className="fa-solid fa-xmark text-xl"/></button>
        </div>

        {error && <div className="text-rose-400 text-xs bg-rose-500/10 border border-rose-500/20 rounded px-3 py-2 mb-3">{error}</div>}

        {phase === "intro" && (
          <div className="space-y-4 font-mono text-sm">
            <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4 text-xs text-slate-300 leading-relaxed">
              You'll get 5 questions on the frozen chart: <strong className="text-purple-300">bias</strong>, <strong className="text-amber-300">FVGs</strong>, <strong className="text-pink-300">SMT</strong>, <strong className="text-emerald-300">premium/discount</strong>, and <strong className="text-blue-300">trade plan</strong>. Claude grades & explains.
            </div>
            <button data-testid="start-quiz-btn" onClick={startQuiz}
              className="w-full py-3 bg-purple-600 hover:bg-purple-700 rounded-xl font-bold text-white">
              <i className="fa-solid fa-play mr-1.5"/> Start Quiz
            </button>
          </div>
        )}

        {phase === "loading" && (
          <div className="py-12 text-center text-slate-400 font-mono text-sm">
            <i className="fa-solid fa-spinner fa-spin text-purple-400 text-2xl mb-3"/>
            <div>Claude is reading the chart and crafting questions…</div>
          </div>
        )}

        {phase === "quiz" && (
          <div className="space-y-4 font-mono text-sm">
            {questions.map((q, i) => (
              <div key={q.id} className="bg-slate-900/60 border border-slate-800 rounded-xl p-3">
                <div className="text-xs text-purple-400 font-bold mb-1.5">Q{i + 1}. {q.prompt}</div>
                {q.type === "mcq" ? (
                  <div className="space-y-1.5">
                    {q.options.map(opt => (
                      <label key={opt} data-testid={`opt-${q.id}-${opt.slice(0,8)}`}
                        className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer border transition-all ${answers[q.id] === opt ? "border-purple-500 bg-purple-500/10 text-white" : "border-slate-800 bg-slate-900/40 text-slate-300 hover:border-slate-600"}`}>
                        <input type="radio" name={q.id} checked={answers[q.id] === opt} onChange={() => setAnswers({ ...answers, [q.id]: opt })} className="accent-purple-500"/>
                        <span className="text-xs">{opt}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <textarea data-testid={`text-${q.id}`} value={answers[q.id] || ""} onChange={e => setAnswers({ ...answers, [q.id]: e.target.value })}
                    placeholder="Describe your entry, stop, target & reasoning…" rows={3}
                    className="w-full text-xs bg-slate-900 border border-slate-800 rounded p-2 text-slate-200 focus:outline-none focus:border-purple-500"/>
                )}
              </div>
            ))}
            <button data-testid="submit-quiz-btn" onClick={submit} disabled={Object.keys(answers).length < questions.length}
              className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 rounded-xl font-bold text-white text-sm">
              Submit for Grading
            </button>
          </div>
        )}

        {phase === "grading" && (
          <div className="py-12 text-center text-slate-400 font-mono text-sm">
            <i className="fa-solid fa-brain fa-beat text-purple-400 text-2xl mb-3"/>
            <div>Claude is grading…</div>
          </div>
        )}

        {phase === "result" && result && (
          <div className="space-y-3 font-mono text-sm">
            <div className="bg-gradient-to-br from-purple-600/20 to-emerald-600/20 border border-purple-500/30 rounded-xl p-4 text-center">
              <div className="text-[10px] text-slate-400 uppercase tracking-wider">Score</div>
              <div data-testid="quiz-score" className="text-4xl font-black text-white">{result.score}<span className="text-slate-500 text-lg">/100</span></div>
              <div className="text-xs text-slate-300 mt-2">{result.overall_feedback}</div>
            </div>
            {result.per_question?.map((pq, i) => (
              <div key={pq.id} className={`rounded-xl p-2.5 border text-[11px] ${pq.correct ? "bg-emerald-500/5 border-emerald-500/30" : "bg-rose-500/5 border-rose-500/30"}`}>
                <div className="font-bold">
                  <i className={`fa-solid ${pq.correct ? "fa-check text-emerald-400" : "fa-xmark text-rose-400"} mr-1.5`}/>
                  Q{i + 1}: {pq.correct ? "Correct" : "Incorrect"}
                </div>
                <div className="text-slate-300 mt-1">{pq.feedback}</div>
              </div>
            ))}
            <button onClick={onClose} className="w-full py-2 bg-blue-600 hover:bg-blue-700 rounded-xl font-bold text-white text-sm">
              Acknowledge
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
